import React from 'react';
import ReactDOM from 'react-dom/client';
import './src/index.css';
import App from './App';
import { initFirebasePerformance } from './utils/firebase';
import {
  SENTRY_DENY_URLS,
  SENTRY_NOISE_MESSAGE_PATTERNS,
  shouldDropSentryEvent,
} from './utils/sentryEventFilters';
import { initPosthog } from './utils/posthog';
import './src/i18n/config';

// Initialize Firebase Performance Monitoring early
initFirebasePerformance();

// Initialize PostHog early (non-blocking, autocapture pageviews)
initPosthog();

// Lazy initialize Sentry after first paint for better performance
const initSentry = async () => {
  // Only load Sentry in production, skip in dev for faster development
  if (import.meta.env.DEV) {
    return;
  }

  const Sentry = await import("@sentry/react");

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,

    // Enable performance monitoring (reduced in production)
    integrations: [
      Sentry.browserTracingIntegration({
        // Reduce tracing overhead
        enableLongTask: false,
        enableInp: false,
      }),
      // Only enable replay on errors, not all sessions
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Adjust sample rates for production (reduced)
    tracesSampleRate: 0.05, // Reduced from 0.1 to 0.05

    // Session Replay (reduced)
    replaysSessionSampleRate: 0.05, // Reduced from 0.1
    replaysOnErrorSampleRate: 1.0,

    // Environment
    environment: import.meta.env.MODE,

    // Send default PII
    sendDefaultPii: true,

    denyUrls: SENTRY_DENY_URLS,
    ignoreErrors: [...SENTRY_NOISE_MESSAGE_PATTERNS],

    beforeSend(event) {
      if (shouldDropSentryEvent(event)) {
        return null;
      }

      if (event.level === 'error' || event.level === 'fatal') {
        return event;
      }
      return null;
    },
  });
};

// Initialize Sentry after page load and idle callback for better initial load
// Further delay Sentry initialization to improve TBT
const initSentryDelayed = () => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      // Additional delay to ensure page is fully loaded and interactive
      setTimeout(() => initSentry(), 3000); // Increased from 2000 to 3000ms
    }, { timeout: 10000 }); // Increased timeout
  } else {
    // Fallback: wait for page load + additional delay
    if (document.readyState === 'complete') {
      setTimeout(() => initSentry(), 3000); // Increased delay
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => initSentry(), 3000); // Increased delay
      });
    }
  }
};

initSentryDelayed();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);